import {
  ABILITY_COUNT,
  Abilities,
  Actor,
  Bracing,
  Buff,
  Casting,
  Dashing,
  Falling,
  Hazard,
  Instability,
  Launch,
  Pickup,
  PlayerTag,
  Projectile,
  Renderable,
  Shockwave,
  Stunned,
  Transform,
  Velocity,
  VortexField,
  type CastPhase,
  type PickupKind,
} from '@/components'
import type { ComponentType } from '@/core/ecs/ComponentStore'
import { NULL_ENTITY, type Entity } from '@/core/ecs/Entity'
import type { World } from '@/core/ecs/World'
import { angleDelta } from '@/core/math/mathUtils'
import type { SceneBridge } from '@/gfx/SceneBridge'
import { round, type EntityView, type NetEvent, type VisualSpawn } from './protocol'

type Values = Array<number | string>

/** A synced component travels under its own name, e.g. `"Velocity": [1.2, 0]`. */
interface Codec<T> {
  type: ComponentType<T>
  write(component: T): Values
  read(component: T, values: Values): void
}

function codec<T>(c: Codec<T>): Codec<T> {
  return c
}

const num = (values: Values, i: number): number => values[i] as number

/**
 * Every component presentation reads, and exactly the fields it reads: the
 * render sync, VFX, audio, HUD and camera run unchanged on a replica built
 * from these. A component or field missing here is invisible online, however
 * right it is on the server — CLAUDE.md §11. A component with no fields
 * travels as its presence alone. On the wire each one is keyed by its name;
 * the socket's deflate makes the repetition cheap.
 */
export const SYNCED: ReadonlyArray<Codec<any>> = [
  codec({
    type: Actor,
    write: (a) => [round(a.gait, 2)],
    read: (a, v) => {
      a.gait = num(v, 0)
    },
  }),
  codec({
    type: Velocity,
    write: (vel) => [round(vel.x, 2), round(vel.z, 2)],
    read: (vel, v) => {
      vel.x = num(v, 0)
      vel.z = num(v, 1)
    },
  }),
  codec({
    type: PlayerTag,
    write: (t) => [t.player, t.color],
    read: (t, v) => {
      t.player = num(v, 0)
      t.color = num(v, 1)
    },
  }),
  codec({
    type: Instability,
    write: (i) => [round(i.value, 1)],
    read: (i, v) => {
      i.value = num(v, 0)
    },
  }),
  codec({
    type: Abilities,
    write: (a) => [
      ...a.charges,
      ...a.maxCharges,
      ...Array.from(a.recharge, (r) => round(r, 2)),
    ],
    read: (a, v) => {
      for (let i = 0; i < ABILITY_COUNT; i++) {
        a.charges[i] = num(v, i)
        a.maxCharges[i] = num(v, ABILITY_COUNT + i)
        a.recharge[i] = num(v, 2 * ABILITY_COUNT + i)
      }
    },
  }),
  codec({
    type: Buff,
    write: (b) => [round(b.shield, 2), round(b.haste, 2)],
    read: (b, v) => {
      b.shield = num(v, 0)
      b.haste = num(v, 1)
    },
  }),
  codec({
    type: Bracing,
    write: (b) => [round(b.timeLeft, 2)],
    read: (b, v) => {
      b.timeLeft = num(v, 0)
    },
  }),
  codec({ type: Dashing, write: () => [], read: () => undefined }),
  codec({ type: Stunned, write: () => [], read: () => undefined }),
  codec({ type: Falling, write: () => [], read: () => undefined }),
  codec({
    type: Casting,
    write: (c) => [c.ability, c.phase],
    read: (c, v) => {
      c.ability = num(v, 0)
      c.phase = v[1] as CastPhase
    },
  }),
  codec({
    type: Launch,
    write: (l) => [round(l.speed, 1), l.bounces],
    read: (l, v) => {
      l.speed = num(v, 0)
      l.bounces = num(v, 1)
    },
  }),
  codec({
    type: Shockwave,
    write: (w) => [round(w.radius, 2), round(w.maxRadius, 2)],
    read: (w, v) => {
      w.radius = num(v, 0)
      w.maxRadius = num(v, 1)
    },
  }),
  codec({
    type: VortexField,
    write: (f) => [f.phase, round(f.timer, 2)],
    read: (f, v) => {
      f.phase = v[0] as 'arming' | 'pulling' | 'burst'
      f.timer = num(v, 1)
    },
  }),
  codec({
    type: Hazard,
    write: (h) => [round(h.interval, 2), round(h.timer, 2), round(h.flash, 2)],
    read: (h, v) => {
      h.interval = num(v, 0)
      h.timer = num(v, 1)
      h.flash = num(v, 2)
    },
  }),
  codec({
    type: Pickup,
    write: (p) => [p.kind, round(p.age, 2)],
    read: (p, v) => {
      p.kind = v[0] as PickupKind
      p.age = num(v, 1)
    },
  }),
  codec({
    type: Projectile,
    write: (p) => [round(p.life, 2)],
    read: (p, v) => {
      p.life = num(v, 0)
    },
  }),
]

// ──────────────────────────────────────────────────────────────── server ──

/**
 * Every entity with a transform, as the snapshot carries it.
 *
 * The transform is the one the server would render right now — blended
 * `alpha` of the way into the current fixed step, like the render sync does
 * offline — so a step frozen by hit stop still moves smoothly on the wire.
 */
export function writeEntities(
  world: World,
  alpha: number,
  visuals: ReadonlyMap<number, VisualSpawn>,
): EntityView[] {
  const out: EntityView[] = []
  for (const e of world.query([Transform]).entities()) {
    const tr = world.read(e, Transform)
    const view: EntityView = {
      id: e,
      pose: [
        round(tr.prevX + (tr.x - tr.prevX) * alpha, 2),
        round(tr.prevY + (tr.y - tr.prevY) * alpha, 2),
        round(tr.prevZ + (tr.z - tr.prevZ) * alpha, 2),
        round(tr.prevAngle + angleDelta(tr.prevAngle, tr.angle) * alpha, 3),
      ],
      components: {},
    }
    const renderable = world.get(e, Renderable)
    const visual = renderable ? visuals.get(renderable.objectId) : undefined
    if (visual) view.visual = visual
    for (const { type, write } of SYNCED) {
      const component = world.get(e, type)
      if (component !== undefined) view.components[type.name] = write(component)
    }
    out.push(view)
  }
  return out
}

// ──────────────────────────────────────────────────────────────── client ──

/**
 * A client's copy of the server's world, built from snapshots.
 *
 * Entities are created and destroyed by server id, and their visuals by the
 * same scene-bridge calls the prefabs make offline, so everything downstream —
 * render sync, VFX, audio, HUD, camera — runs on it unchanged.
 */
export class Replica {
  private readonly local = new Map<number, Entity>()

  constructor(
    private readonly world: World,
    private readonly bridge: SceneBridge | null,
  ) {}

  /** The local entity for a server id, or `NULL_ENTITY` if it is not here. */
  entity(id: number): Entity {
    const e = this.local.get(id)
    return e !== undefined && this.world.isAlive(e) ? e : NULL_ENTITY
  }

  /**
   * Makes the replica match `to`, with every transform set to blend from where
   * `from` had it: the render sync then draws previous → current at the
   * buffer's alpha, exactly as it draws two fixed steps offline.
   */
  apply(from: readonly EntityView[] | null, to: readonly EntityView[]): void {
    const world = this.world
    const previous = new Map<number, EntityView['pose']>()
    if (from) for (const view of from) previous.set(view.id, view.pose)
    const seen = new Set<number>()

    for (const view of to) {
      seen.add(view.id)
      let e = this.local.get(view.id)
      if (e === undefined || !world.isAlive(e)) {
        e = this.spawn(view)
        this.local.set(view.id, e)
      }

      const tr = world.read(e, Transform)
      const prev = previous.get(view.id) ?? view.pose
      tr.prevX = prev[0]
      tr.prevY = prev[1]
      tr.prevZ = prev[2]
      tr.prevAngle = prev[3]
      tr.x = view.pose[0]
      tr.y = view.pose[1]
      tr.z = view.pose[2]
      tr.angle = view.pose[3]

      for (const { type, read } of SYNCED) {
        const values = view.components[type.name]
        if (values) read(world.get(e, type) ?? world.add(e, type), values)
        else if (world.has(e, type)) world.remove(e, type)
      }
    }

    for (const [id, e] of this.local) {
      if (seen.has(id)) continue
      world.destroyEntity(e)
      this.local.delete(id)
    }
    // Gone this frame, not at the end of the next stage: presentation must not
    // draw an entity the snapshot no longer has.
    world.flushDestroyed()
  }

  /** Destroys everything; the next snapshot rebuilds from scratch. */
  reset(): void {
    for (const e of this.local.values()) this.world.destroyEntity(e)
    this.local.clear()
    this.world.flushDestroyed()
  }

  /** An event's payload with its entity handles rewritten to this replica's. */
  event(event: NetEvent): Record<string, unknown> {
    const data: Record<string, unknown> = { ...event.data }
    for (const key of ['entity', 'source', 'target']) {
      if (typeof data[key] === 'number') data[key] = this.entity(data[key] as number)
    }
    return data
  }

  private spawn(view: EntityView): Entity {
    const e = this.world.createEntity()
    this.world.add(e, Transform)
    if (view.visual && this.bridge) {
      this.world.add(e, Renderable, { objectId: createVisual(this.bridge, view.visual) })
    }
    return e
  }
}

/** Replays on a client the scene-bridge call the server's prefab made. */
export function createVisual(bridge: SceneBridge, v: VisualSpawn): number {
  switch (v[0]) {
    case 'character':
      return bridge.addCharacter(v[1], v[2], v[3])
    case 'prop':
      return bridge.addProp(v[1], v[2], v[3])
    case 'vortex':
      return bridge.addVortexField(v[1], v[2])
    case 'hazard':
      return bridge.addHazard(v[1], v[2])
    case 'shockwave':
      return bridge.addShockwave(v[1])
    case 'bolt':
      return bridge.addBolt(v[1])
    case 'pickup':
      return bridge.addPickup(v[1])
  }
}
